import React, { useEffect, useMemo } from 'react';
import { HbrDonutChart } from '@harbor/elements-react-standalone';
import { TileDefinition } from '../../../types/dashboard';
import type { TileSettings } from '../../../types/tileBuilderTypes';
import { buildHarborItemTooltip } from '../../../support/harborTooltip';
import { getContextualChartMargins } from './chartLayoutUtils';
import { makeSmartValueFormatter } from './tileUtils';

interface ChartData {
  id: string;
  label: string;
  value: number;
}

interface GaugeChartTileProps {
  data: ChartData[];
  tileDef: TileDefinition;
  layoutHeight: number;
  layoutWidth: number;
  setError?: (error: string) => void;
  initialized?: boolean;
  displayContext?: 'preview' | 'dashboard' | 'leader';
}

const GaugeChartTile: React.FC<GaugeChartTileProps> = ({
                                                         tileDef,
                                                         data,
                                                         layoutHeight,
                                                         layoutWidth,
                                                         setError,
                                                         initialized = false,
                                                         displayContext = 'dashboard',
                                                       }) => {
  const s = useMemo(() => (tileDef?.settings || {}) as TileSettings & any, [tileDef?.settings]);

  const isValidArray = Array.isArray(data);
  const dataArray = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const w = Math.max(1, layoutWidth || 0);
  const h = Math.max(1, layoutHeight || 0);

  useEffect(() => {
    if (initialized && !isValidArray) {
      console.error('Expected gauge data to be an array, but got:', data);
      setError?.('Invalid data format');
    }
  }, [initialized, isValidArray, data, setError]);

  const total = useMemo(
    () => dataArray.reduce((acc, cur) => acc + (Number(cur?.value) || 0), 0),
    [dataArray]
  );

  const valueFormatter = useMemo(
    () =>
      makeSmartValueFormatter({
        valueFormat: s?.valueFormat,
        currency: s?.currency,
        numberNotation: s?.numberNotation,
        compactThreshold: s?.compactThreshold,
        compactDecimals: s?.compactDecimals,
        prefixCurrencyOnCompact: s?.prefixCurrencyOnCompact,
      }),
    [
      s?.valueFormat,
      s?.currency,
      s?.numberNotation,
      s?.compactThreshold,
      s?.compactDecimals,
      s?.prefixCurrencyOnCompact,
    ]
  );

  const showLegend = useMemo(() => {
    return layoutHeight > 150 && !!(tileDef?.show_legend || s?.show_legend);
  }, [layoutHeight, tileDef?.show_legend, s?.show_legend]);

  const layoutStrategy: 'auto' | 'manual' = s?.layoutStrategy === 'manual' ? 'manual' : 'auto';

  const computedMargin = useMemo(() => {
    const manualMargin = getContextualChartMargins(s, displayContext, showLegend, {
      top: 30,
      right: 10,
      bottom: 55,
      left: showLegend ? 150 : 10,
    });

    if (layoutStrategy === 'manual' && manualMargin) {
      return manualMargin;
    }

    return {
      top: typeof s?.marginTop === 'number' ? s.marginTop : 30,
      right: typeof s?.marginRight === 'number' ? s.marginRight : 10,
      bottom: typeof s?.marginBottom === 'number' ? s.marginBottom : 55,
      left: typeof s?.marginLeft === 'number' ? s.marginLeft : showLegend ? 150 : 10,
    };
  }, [
    s,
    displayContext,
    showLegend,
    layoutStrategy,
  ]);

  const innerRadius = useMemo(
    () => (typeof s?.innerRadius === 'number' ? s.innerRadius : 0.7),
    [s?.innerRadius]
  );
  const startAngle = useMemo(
    () => (typeof s?.startAngle === 'number' ? s.startAngle : -90),
    [s?.startAngle]
  );
  const endAngle = useMemo(
    () => (typeof s?.endAngle === 'number' ? s.endAngle : 90),
    [s?.endAngle]
  );

  const enableArcLinkLabels = !!s?.enableArcLinkLabels;
  const enableArcLabels = !!s?.enableArcLabels;

  const itemTooltip = useMemo(() => {
    return buildHarborItemTooltip(
      s,
      {
        getSeries: (e: any) =>
          e?.id ?? e?.key ?? e?.data?.id ?? e?.data?.key ?? e?.data?.label ?? '',
        getLabel: (e: any) => e?.data?.label ?? e?.label ?? e?.data?.id ?? e?.id ?? '',
        getValue: (e: any) => {
          const raw = e?.data?.value ?? e?.value ?? '';
          const n = Number(raw);
          return Number.isFinite(n) ? valueFormatter(n) : raw;
        },
      },
      { dotMode: 'before' }
    );
  }, [s, valueFormatter]);

  const legends = useMemo(() => {
    if (!showLegend) return [];
    return [
      {
        direction: s?.legendDirection ?? 'column',
        align: 'left',
        translateX: typeof s?.legendTranslateX === 'number' ? s.legendTranslateX : -220,
        translateY: typeof s?.legendTranslateY === 'number' ? s.legendTranslateY : -10,
        itemWidth: typeof s?.legendItemWidth === 'number' ? s.legendItemWidth : 104,
        itemHeight: typeof s?.legendItemHeight === 'number' ? s.legendItemHeight : 20,
        itemsSpacing: typeof s?.legendItemsSpacing === 'number' ? s.legendItemsSpacing : 5,
        symbolSize: typeof s?.legendSymbolSize === 'number' ? s.legendSymbolSize : 18,
      },
    ];
  }, [
    showLegend,
    s?.legendDirection,
    s?.legendTranslateX,
    s?.legendTranslateY,
    s?.legendItemWidth,
    s?.legendItemHeight,
    s?.legendItemsSpacing,
    s?.legendSymbolSize,
  ]);

  const centerContent = useMemo(() => {
    return () => `
      <div style="position:relative;top:-20px;display:flex;height:100%;flex-direction:column;justify-content:center;align-items:center;">
        <div style="font-size:var(--hbr-font-size-2xl);font-weight:var(--hbr-font-weight-bold,700)">${valueFormatter(total)}</div>
      </div>
    `;
  }, [total, valueFormatter]);

  const popoverOptions = useMemo(() => {
    return {
      ...(s?.popoverOptions ?? {}),
      hoist: true,
    };
  }, [s?.popoverOptions]);

  const config = useMemo(() => {
    return {
      data: dataArray,
      margin: computedMargin,
      innerRadius,
      startAngle,
      endAngle,
      legends,
      enableArcLinkLabels,
      enableArcLabels,
      itemTooltip,
      centerContentTransform: 'translate(0, -30)',
      centerContent,
      popoverOptions,
    };
  }, [
    dataArray,
    computedMargin,
    innerRadius,
    startAngle,
    endAngle,
    legends,
    enableArcLinkLabels,
    enableArcLabels,
    itemTooltip,
    centerContent,
    popoverOptions,
  ]);

  const onHbrModifyConfig = useMemo(() => {
    return (currentConfig: any) => ({
      ...currentConfig,
      popoverOptions: {
        ...(currentConfig?.popoverOptions ?? {}),
        ...popoverOptions,
        hoist: true,
      },
    });
  }, [popoverOptions]);

  if (!setError && initialized && !isValidArray) {
    return <div>Invalid data format</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: showLegend ? 'flex-start' : 'center',
        alignItems: 'center',
        height: '100%',
        position: 'relative',
      }}
    >
      <HbrDonutChart
        key={`gauge-${w}x${h}-${displayContext}-${showLegend ? 'legend' : 'base'}`}
        variant="gauge"
        width="100%"
        height="100%"
        config={config}
        modifyHbrConfig={onHbrModifyConfig}
      />
    </div>
  );
};

export default GaugeChartTile;